import pricingJson from "./data/pricing-data.json";
import seasonalJson from "./data/seasonal-demand.json";

export interface JobType {
  slug: string;
  name: string;
  labourLow: number;
  labourHigh: number;
  materialsLow: number;
  materialsHigh: number;
  durationDays: string;
  costFactors: string[];
  scopes: { name: string; multiplier: number }[];
}

export interface TradePricing {
  tradeSlug: string;
  tradeName: string;
  jobTypes: JobType[];
}

export const PRICING_DATA = pricingJson as TradePricing[];

export const SEASONAL_DEMAND = seasonalJson as Record<string, number[]>;

export function getTradePricing(tradeSlug: string): TradePricing | undefined {
  return PRICING_DATA.find((t) => t.tradeSlug === tradeSlug);
}

export function getJobType(
  tradeSlug: string,
  jobSlug: string
): JobType | undefined {
  const trade = getTradePricing(tradeSlug);
  return trade?.jobTypes.find((j) => j.slug === jobSlug);
}

export function estimateCost(
  jobType: JobType,
  scopeIndex: number
): { low: number; high: number } {
  const scope = jobType.scopes[scopeIndex] ?? jobType.scopes[1];
  const totalLow = jobType.labourLow + jobType.materialsLow;
  const totalHigh = jobType.labourHigh + jobType.materialsHigh;
  return {
    low: Math.round(totalLow * scope.multiplier),
    high: Math.round(totalHigh * scope.multiplier),
  };
}

/**
 * Pick a representative mid-range job type for a trade. Used as a sensible
 * fallback when we cannot map a diagnosis to a specific job (e.g. the AI did not
 * return a recognised job slug). Sorts the trade's job types by their mid-point
 * total cost and returns the median, avoiding the cheapest and most expensive
 * extremes.
 */
export function getMedianJobType(tradeSlug: string): JobType | undefined {
  const trade = getTradePricing(tradeSlug);
  if (!trade || trade.jobTypes.length === 0) return undefined;
  const mid = (j: JobType) =>
    (j.labourLow + j.materialsLow + j.labourHigh + j.materialsHigh) / 2;
  const sorted = [...trade.jobTypes].sort((a, b) => mid(a) - mid(b));
  return sorted[Math.floor(sorted.length / 2)];
}

export function getSeasonalDemand(tradeSlug: string, month: number): number {
  const demand = SEASONAL_DEMAND[tradeSlug];
  if (!demand) return 0.7;
  return demand[month] ?? 0.7;
}
